import React, { useState, useEffect } from 'react';

interface ComingSoonOverlayProps {
  onDismiss?: () => void;
}

const ComingSoonOverlay: React.FC<ComingSoonOverlayProps> = ({ onDismiss }) => {
  const [progress, setProgress] = useState(0);
  const [currentFeature, setCurrentFeature] = useState(0);
  const [email, setEmail] = useState('');
  const [isSubscribed, setIsSubscribed] = useState(false);

  const features = [
    {
      icon: 'fas fa-brain',
      title: 'AI-Adaptive Questions',
      description: 'Questions that adapt to your skill level in real-time',
      color: '#667eea'
    },
    {
      icon: 'fas fa-chart-line',
      title: 'Real-time Analytics',
      description: 'Instant performance insights and improvement suggestions',
      color: '#28a745'
    },
    {
      icon: 'fas fa-target',
      title: 'Weakness Detection',
      description: 'AI identifies your weak areas and focuses practice there',
      color: '#ffc107'
    },
    {
      icon: 'fas fa-graduation-cap',
      title: 'Personalized Learning',
      description: 'Study plans tailored specifically to your learning style',
      color: '#dc3545'
    }
  ];

  useEffect(() => {
    // Simulate development progress
    const progressTimer = setInterval(() => {
      setProgress(prev => {
        if (prev >= 85) {
          clearInterval(progressTimer);
          return 85;
        }
        return prev + 1;
      });
    }, 100);

    // Rotate features showcase
    const featureTimer = setInterval(() => {
      setCurrentFeature(prev => (prev + 1) % 4); // Fixed number since features array is constant
    }, 3000);

    return () => {
      clearInterval(progressTimer);
      clearInterval(featureTimer);
    };
  }, []);

  const handleEmailSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (email) {
      setIsSubscribed(true);
      // Here you would normally send to your email service
      setTimeout(() => {
        setEmail('');
        setIsSubscribed(false);
      }, 3000);
    }
  };

  return (
    <div className="position-relative">
      {/* Backdrop */}
      <div 
        className="top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
        style={{ 
          backgroundColor: 'rgba(248, 249, 255, 0.95)',
          backdropFilter: 'blur(5px)',
          zIndex: 10,
          minHeight: '400px'
        }}
      >
        <div className="text-center p-0">
          {/* Main Coming Soon Message */}
          <div className="mb-4">
            <div className="d-inline-flex align-items-center justify-content-center rounded-circle mb-3"
                 style={{ 
                   width: '80px', 
                   height: '80px',
                   background: 'linear-gradient(135deg, #667eea, #764ba2)'
                 }}>
              <i className="fas fa-rocket text-white fa-2x"></i>
            </div>
            <h2 className="fw-bold mb-3" style={{ 
              background: 'linear-gradient(135deg, #667eea, #764ba2)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text'
            }}>
              AI-Powered Mock Tests Coming Soon!
            </h2>
            <p className="text-muted fs-5 mb-4">
              We're building something revolutionary that will transform how you prepare for exams
            </p>
          </div>

          {/* Progress Bar */}
          <div className="mb-4">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <span className="small text-muted">Development Progress</span>
              <span className="small fw-bold text-primary">{progress}% Complete</span>
            </div>
            <div className="progress" style={{ height: '8px', borderRadius: '10px' }}>
              <div 
                className="progress-bar progress-bar-striped progress-bar-animated"
                style={{ 
                  width: `${progress}%`,
                  background: 'linear-gradient(90deg, #667eea, #764ba2)',
                  borderRadius: '10px'
                }}
              ></div>
            </div>
          </div>

          {/* Feature Showcase */}
          <div className="mb-4">
            <div className="card border-0 shadow-sm" style={{ borderRadius: '15px', minHeight: '120px' }}>
              <div className="card-body p-4">
                <div className="d-flex align-items-center">
                  <div 
                    className="rounded-circle d-flex align-items-center justify-content-center me-3"
                    style={{ 
                      width: '50px', 
                      height: '50px',
                      backgroundColor: features[currentFeature].color + '20'
                    }}
                  >
                    <i 
                      className={`${features[currentFeature].icon} fa-lg`}
                      style={{ color: features[currentFeature].color }}
                    ></i>
                  </div>
                  <div className="text-start">
                    <h6 className="fw-bold mb-1">{features[currentFeature].title}</h6>
                    <p className="text-muted mb-0 small">{features[currentFeature].description}</p>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Feature Indicators */}
            <div className="d-flex justify-content-center mt-3 gap-2">
              {features.map((_, index) => (
                <div
                  key={index}
                  className={`rounded-pill ${index === currentFeature ? 'bg-primary' : 'bg-light'}`}
                  style={{ 
                    width: '8px', 
                    height: '8px',
                    transition: 'all 0.3s ease'
                  }}
                ></div>
              ))}
            </div>
          </div>

                   

          {/* Timeline */}
          <div className="mb-4">
            <small className="text-muted">
              <i className="fas fa-calendar-alt me-1"></i>
              Expected Launch: Next Month
            </small>
          </div>

          {/* Action Buttons */}
          <div className="d-flex gap-2 justify-content-center">
            <button 
              className="btn btn-outline-primary"
              onClick={onDismiss}
              style={{ borderRadius: '25px' }}
            >
              <i className="fas fa-eye me-1"></i>
              Preview Mode
            </button>
            <button 
              className="btn btn-primary"
              style={{ 
                borderRadius: '25px',
                background: 'linear-gradient(135deg, #667eea, #764ba2)',
                border: 'none'
              }}
            >
              <i className="fas fa-bell me-1"></i>
              Get Updates
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ComingSoonOverlay;